<template>
	<v-app>
		<v-main>
			<v-container class="widget-layout" :class="{ 'widget-layout--fullscreen-map': isMapWidgetFullscreen }">
				<div class="widget-layout-grid">
					<template v-if="sortedWidgets">
						<component v-for="widget in sortedWidgets" :key="widget.id" :is="widgetComponents[widget.componentKey]" :ref="(el) => registerWidgetRef(el, widget.id.toLowerCase())" :title="widget.title" :params="widget.params" :guid="widget.guid" :style="getWidgetGridStyle(widget)" v-show="widget.visible" />
					</template>
					<template v-else>
						<div v-for="skeleton in skeletonWidgets" :key="skeleton.id" class="widget widget-skeleton" :class="{ 'widget-skeleton--circle': skeleton.circle }" :style="getWidgetGridStyle(skeleton)">
							<v-progress-circular v-if="skeleton.circle" indeterminate color="grey-lighten-1" :width="12" />
							<v-card v-else class="mx-auto" flat>
								<v-skeleton-loader :type="skeleton.type" />
							</v-card>
						</div>
						<v-snackbar v-model="widgetsLoading" :timeout="-1" :loading="true" rounded="xl" color="info">
							{{ t('widgetsLoading') }}
						</v-snackbar>
					</template>
				</div>

				<DialogControl v-model="widgetSettingsDialog" :attach="overlayTarget">
					<v-card>
						<v-card-title class="headline">
							<v-icon>widgets</v-icon>
							{{ t('WidgetsList') }}
						</v-card-title>
						<v-card-text>
							<AgGrid ref="widgetSettingsGrid" v-model:rowData="sortedWidgets" :colDefs="widgetSettingsGridColumns" row-group-panel-show="false"></AgGrid>
						</v-card-text>
						<v-card-actions>
							<v-btn color="error" variant="text" @click="resetWidgetSettings">{{ t('Reset') }}</v-btn>
							<v-btn text @click="widgetSettingsDialog = false" data-close>{{ t('Close') }}</v-btn>
							<v-btn variant="tonal" @click="saveWidgetSettings">{{ t('Save') }}</v-btn>
						</v-card-actions>
					</v-card>
				</DialogControl>
			</v-container>
		</v-main>
	</v-app>
</template>

<script setup>
import { ref, watch, inject, computed, onMounted, onBeforeUnmount, provide, markRaw, nextTick } from 'vue';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { useTranslator } from '@/composables/useTranslator';
import { eventBus } from '@/utils/core/eventBus';
import { deviceUtils } from '@/utils/core/deviceUtils';
import { isEmbeddedInBackOffice } from '@/utils/core/boContext';
import { getUserSettings, postUserSettings } from '@/api/core/userSetting';
import { getWidgets, WidgetEnum } from '@/api/core/widget';
import { isSyncCompleted, isBackgroundSyncCompleted } from '@/sync/dataSync';
import { kpiService } from '@/services/kpiService';

import WidgetMyDay from './WidgetMyDay.vue';
import WidgetMyArea from './WidgetMyArea.vue';
import WidgetKpi from './WidgetKpi.vue';
import WidgetSps from './WidgetSps.vue';
import WidgetOrderTracking from './WidgetOrderTracking.vue';
import AgGrid from '../agGrid/AgGrid.vue';
import DialogControl from '@/components/controls/DialogControl.vue';
import VisibilitySwitchRenderer from '@/components/agGrid/VisibilitySwitchRenderer.vue';
import ButtonIconRenderer from '@/components/agGrid/ButtonIconRenderer.vue';
import { log } from '@/utils/core/vueLogger';
import { getUserPoints, formatPoints } from '@/api/sales/points';
import { isScoringMatrixAvailable } from '@/services/pointsMatrixService';
import { spsService } from '@/services/spsService';

// #region DATA

const t = useTranslator();
const overlayTarget = inject('overlayTarget', null);

const widgetComponents = {
	WidgetMyDay: markRaw(WidgetMyDay),
	WidgetMyArea: markRaw(WidgetMyArea),
	WidgetKpi: markRaw(WidgetKpi),
	WidgetSps: markRaw(WidgetSps),
	WidgetOrderTracking: markRaw(WidgetOrderTracking),
};

const iframeDocument = iframeDataBridge.getIframeWindow().document;
const user = iframeDataBridge.getCurrentUser();
const userSettings = ref([]);
const widgetsLoading = ref(true);
const isTouchMode = ref(deviceUtils.isTouchMode());
const skeletonWidgets = [
	{ id: 'skeleton-myday', columns: 2, rows: 2, type: 'card, list-item, list-item-two-line, list-item-two-line' },
	{ id: 'skeleton-myarea', columns: 2, rows: 2, type: 'list-item, list-item-two-line' },
	{ id: 'skeleton-kpi-1', columns: 1, rows: 1, circle: true },
	{ id: 'skeleton-kpi-2', columns: 1, rows: 1, circle: true },
	{ id: 'skeleton-kpi-3', columns: 2, rows: 2, circle: true },
	{ id: 'skeleton-kpi-4', columns: 1, rows: 1, circle: true },
	{ id: 'skeleton-kpi-5', columns: 1, rows: 1, circle: true },
	{ id: 'skeleton-kpi-6', columns: 1, rows: 1, circle: true },
	{ id: 'skeleton-kpi-7', columns: 1, rows: 1, circle: true },
];
const widgetSettingsDialog = ref(false);
const widgetSettingsGrid = ref(null);
const widgetSettingsGridColumns = ref([]);
const activeGridColumns = ref(getGridColumnsForViewport(window.innerWidth));
const fullscreenWidget = ref(null);
const MAP_WIDGET_IDS = ['widget-myday', 'widget-myarea'];
const isMapWidgetFullscreen = computed(() => MAP_WIDGET_IDS.includes(fullscreenWidget.value) && isTouchMode.value);

const widgetRefs = new Map();
function registerWidgetRef(el, id) {
	if (el) widgetRefs.set(id.toLowerCase(), el);
	else widgetRefs.delete(id.toLowerCase());
}

const mapControl = {
	zoomToLocation: (locationId) => {},
	drawRoutes: (locations) => {},
};

provide('mapControl', mapControl);

// #endregion

// #region LIFECYCLE

const init = async () => {
	activeGridColumns.value = getGridColumnsForViewport(window.innerWidth);
	injectWidgetSettingsMenu();
	injectPointsBadge();
	await getWidgetSettings();
	setWidgetSettingsGridData();

	eventBus.on('widget-fullscreened', resizeLayout);
	eventBus.on('kpi-simulated-changed', onKpiSimulatedChanged);
	eventBus.on('custom-kpi-saved', onKpiCustomSaved);
	eventBus.on('vue-persistent-zone-replaced', onPersistentZoneReplaced);
	window.addEventListener('resize', handleWindowResize);
	window.addEventListener('points-claimed', injectPointsBadge);
	window.addEventListener('points-updated', injectPointsBadge);
};

const onOpenWidgetDialog = () => {
	widgetSettingsDialog.value = true;
};

const isFullySynced = () => isSyncCompleted() && isBackgroundSyncCompleted();

const initWhenFullySynced = () => {
	if (!isFullySynced()) return;
	eventBus.off('main-sync-completed', initWhenFullySynced);
	eventBus.off('background-sync-completed', initWhenFullySynced);
	init();
};

onMounted(() => {
	if (isEmbeddedInBackOffice()) {
		log('info', 'WidgetLayout', 'Embedded in Back-Office — skipping widget load');
		return;
	}

	window.addEventListener('open-vue-widget-dialog', onOpenWidgetDialog);

	if (isFullySynced()) {
		init();
	} else {
		eventBus.on('main-sync-completed', initWhenFullySynced);
		eventBus.on('background-sync-completed', initWhenFullySynced);
	}
});

onBeforeUnmount(() => {
	eventBus.off('main-sync-completed', initWhenFullySynced);
	eventBus.off('background-sync-completed', initWhenFullySynced);
	eventBus.off('widget-fullscreened', resizeLayout);
	eventBus.off('kpi-simulated-changed', onKpiSimulatedChanged);
	eventBus.off('custom-kpi-saved', onKpiCustomSaved);
	eventBus.off('vue-persistent-zone-replaced', onPersistentZoneReplaced);
	window.removeEventListener('resize', handleWindowResize);
	window.removeEventListener('points-claimed', injectPointsBadge);
	window.removeEventListener('points-updated', injectPointsBadge);
	window.removeEventListener('open-vue-widget-dialog', onOpenWidgetDialog);
});

// #endregion

// #region FULLSCREEN HEADER

watch(
	fullscreenWidget,
	(widget) => {
		const body = iframeDataBridge.getIframeWindow()?.document?.body;
		if (!body) return;
		body.classList.toggle('widget-fullscreen', widget !== null);
	},
	{ immediate: true, flush: 'sync' }
);

// #endregion

// #region SETTINGS

const baseWidgetsTemplate = [
	{ id: 'widget-myday', key: WidgetEnum.MyDay, component: 'WidgetMyDay', title: t('Home_My_Planning'), visible: true, mandatory: true, columns: 2, rows: 2, index: 1 },
	{ id: 'widget-myArea', key: WidgetEnum.MyArea, component: 'WidgetMyArea', title: t('Home_Scope'), visible: true, mandatory: false, columns: 2, rows: 2, index: 2 },
];

const baseWidgetIds = new Set([...baseWidgetsTemplate.map((w) => w.id), 'widget-sps', 'widget-order-tracking']);
const CUSTOM_KPIS_SETTING_NAME = 'VueCustomKpis';

function buildKpiWidgets(startIndex = 3) {
	try {
		const kpiList = kpiService.getAvailableKpis(true);
		return kpiList.map((kpi, idx) => {
			const reference = (kpi.reference || '').toLowerCase();
			const widgetId = `widget-${reference}`;

			return {
				id: widgetId,
				key: reference,
				component: 'WidgetKpi',
				title: kpi.referenceTranslated || kpi.reference,
				visible: true,
				mandatory: false,
				columns: 1,
				rows: 1,
				index: startIndex + idx,
				params: {
					type: 'kpi',
					shape: 'circle',
					reference: kpi.reference,
					icon: kpi.icon || 'star',
					simulated: false,
				},
			};
		});
	} catch (error) {
		log('error', 'WidgetLayout', `Error building KPI widgets: ${error.message}`);
		return [];
	}
}

// SPS is a fixed widget (like MyDay/MyArea), not one of the auto-generated KPI widgets — it's reconstructed
// from spsService each load rather than persisted as a custom widget, so its id belongs in baseWidgetIds.
function buildSpsWidget() {
	try {
		if (!spsService.isAvailable()) return [];

		return [
			{
				id: 'widget-sps',
				key: WidgetEnum.SPS,
				component: 'WidgetSps',
				title: t('SPS'),
				visible: true,
				mandatory: false,
				columns: 1,
				rows: 1,
				index: 3,
				params: {
					type: 'sps',
					shape: 'circle',
				},
			},
		];
	} catch (error) {
		log('error', 'WidgetLayout', `Error building SPS widget: ${error.message}`);
		return [];
	}
}

function buildOrderTrackingWidget() {
	try {
		if (!iframeDataBridge.getSalesHelper()?.isProductAssortOrderActivated?.()) return [];

		return [
			{
				id: 'widget-order-tracking',
				key: WidgetEnum.OrderTracking,
				component: 'WidgetOrderTracking',
				title: t('DirectOrderTracking'),
				visible: true,
				mandatory: false,
				columns: 2,
				rows: 2,
				index: 4,
				params: { type: 'orderTracking' },
			},
		];
	} catch (error) {
		log('error', 'WidgetLayout', `Error building order tracking widget: ${error.message}`);
		return [];
	}
}

let widgetsConfig = [];
const sortedWidgets = ref(null);
provide('sortedWidgets', sortedWidgets);

function getWidgetMinSpan(widgetId, dimension = 'columns') {
	return ['widget-myday', 'widget-myArea', 'widget-order-tracking'].includes(widgetId) ? 2 : 1;
}

function clampGridSpan(value, widgetId = null, dimension = 'columns') {
	const parsed = Number.parseInt(value, 10);
	const min = widgetId ? getWidgetMinSpan(widgetId, dimension) : 1;
	if (Number.isNaN(parsed)) {
		return min;
	}

	return Math.min(Math.max(parsed, min), 6);
}

function getGridColumnsForViewport(width) {
	if (width >= 1920) return 9;
	if (width >= 1600) return 8;
	if (width >= 1280) return 7;
	if (width >= 1100) return 6;
	if (width >= 960) return 5;
	if (width >= 800) return 4;
	if (width >= 600) return 3;
	return 2;
}

function getWidgetGridStyle(widget) {
	const widgetColumns = clampGridSpan(widget?.columns);
	const widgetRows = clampGridSpan(widget?.rows);
	const effectiveColumns = Math.min(widgetColumns, activeGridColumns.value);

	return {
		gridColumn: `span ${effectiveColumns}`,
		gridRow: `span ${widgetRows}`,
	};
}

/**
 * Drops the KPI builder's saved custom KPIs (VueCustomKpis) whose underlying KPI has been deactivated
 * or deleted in the Back-Office. Mutates the setting in place — the caller batches the single save.
 * @returns {number} number of removed entries
 */
function pruneDeactivatedCustomKpiSettings() {
	const customKpiSettings = userSettings.value.find((s) => s.name === CUSTOM_KPIS_SETTING_NAME);
	if (!customKpiSettings?.value) return 0;

	try {
		const savedCustomKpis = JSON.parse(customKpiSettings.value);
		if (!Array.isArray(savedCustomKpis)) return 0;

		const keptCustomKpis = savedCustomKpis.filter((customKpi) => !customKpi?.params?.reference || kpiService.isKpiActivated(customKpi.params.reference));
		if (keptCustomKpis.length === savedCustomKpis.length) return 0;

		customKpiSettings.value = JSON.stringify(keptCustomKpis);
		return savedCustomKpis.length - keptCustomKpis.length;
	} catch (error) {
		log('warn', 'WidgetLayout', `Could not prune saved custom KPIs: ${error.message}`);
		return 0;
	}
}

async function getWidgetSettings() {
	userSettings.value = await getUserSettings({ groupName: 'WEBAPP', userId: user.id });
	const spsWidgets = buildSpsWidget();
	const orderTrackingWidgets = buildOrderTrackingWidget();
	widgetsConfig = [...baseWidgetsTemplate, ...spsWidgets, ...orderTrackingWidgets, ...buildKpiWidgets(3 + spsWidgets.length + orderTrackingWidgets.length)];
	let widgetSettings = userSettings.value.find((s) => s.name === 'VueWidgets');

	// Only prune once the KPI definitions are actually loaded — against a half-synced ecosVM every
	// reference would look deactivated and the user's whole layout would be wiped.
	const canPruneDeactivatedKpis = kpiService.hasKpiDefinitions();
	const removedCustomKpiCount = canPruneDeactivatedKpis ? pruneDeactivatedCustomKpiSettings() : 0;
	let removedWidgetSettingCount = 0;

	if (!widgetSettings) {
		widgetSettings = {
			name: 'VueWidgets',
			groupName: 'WEBAPP',
			value: JSON.stringify(widgetsConfig),
		};
		user.settings.push(widgetSettings);
	} else {
		const settings = JSON.parse(widgetSettings.value);

		// A KPI deactivated (or deleted) in the Back-Office disappears from getAvailableKpis(true), so its
		// saved entry falls through to the custom-KPI restore below and comes back on every home load as a
		// widget that can never load data. Drop those entries from the user settings for good.
		const deactivatedKpiSettingIds = canPruneDeactivatedKpis ? new Set(settings.filter((s) => s.params?.reference && !kpiService.isKpiActivated(s.params.reference)).map((s) => s.id)) : new Set();
		const activeSettings = deactivatedKpiSettingIds.size > 0 ? settings.filter((s) => !deactivatedKpiSettingIds.has(s.id)) : settings;
		removedWidgetSettingCount = deactivatedKpiSettingIds.size;

		widgetsConfig.forEach((widget) => {
			const existingWidget = activeSettings.find((w) => w.id === widget.id);

			if (existingWidget) {
				// Keep the freshly-built, translated title (from buildKpiWidgets / baseWidgetsTemplate)
				// instead of the literal title frozen in the saved VueWidgets setting.
				const translatedTitle = widget.title;
				Object.assign(widget, existingWidget);
				widget.title = translatedTitle;
				widget.columns = clampGridSpan(widget.columns, widget.id, 'columns');
				widget.rows = clampGridSpan(widget.rows, widget.id, 'rows');

				if (existingWidget.simulated !== undefined && widget.params && 'simulated' in widget.params) {
					widget.params = { ...widget.params, simulated: existingWidget.simulated };
				}
			}
		});

		// Restore custom KPIs not in the base config. Entries whose KPI is gone or deactivated were already
		// filtered out above, so nothing here can resurrect a widget that can never load data.
		const widgetsConfigIds = new Set(widgetsConfig.map((w) => w.id));
		activeSettings
			.filter((s) => !baseWidgetIds.has(s.id) && s.componentKey && !widgetsConfigIds.has(s.id))
			.forEach((customWidget) => {
				const customParams = normalizeCustomKpiParams(customWidget.params, widgetsConfig);
				widgetsConfig.push({
					id: customWidget.id,
					key: customWidget.id,
					component: customWidget.componentKey,
					title: customWidget.title,
					visible: customWidget.visible ?? true,
					mandatory: false,
					columns: clampGridSpan(customWidget.columns, customWidget.id, 'columns'),
					rows: clampGridSpan(customWidget.rows, customWidget.id, 'rows'),
					index: customWidget.index,
					custom: true,
					guid: customWidget.guid || customWidget.id,
					params: customParams,
				});
			});

		if (removedWidgetSettingCount > 0) {
			widgetSettings.value = JSON.stringify(activeSettings);
		}
	}

	if (removedWidgetSettingCount > 0 || removedCustomKpiCount > 0) {
		await postUserSettings(userSettings.value, { groupName: 'WEBAPP', userId: user.id });
		log('info', 'WidgetLayout', `Removed ${removedWidgetSettingCount} widget setting(s) and ${removedCustomKpiCount} saved custom KPI(s) tied to deactivated or deleted KPI(s)`);
	}

	widgetsConfig = await filterOrganisationWidgets(widgetsConfig);
	sortedWidgets.value = widgetsConfig.sort((a, b) => a.index - b.index).map((w) => ({ ...w, componentKey: w.component }));
}

async function filterOrganisationWidgets(widgetsConfig) {
	try {
		const organisationWidgetParams = await getWidgets({ id: user.organisationId });

		if (organisationWidgetParams) {
			organisationWidgetParams.widgets.forEach((organisationWidget) => {
				// Remove widgets that are hidden by the organisation
				if (organisationWidget.state == 2) {
					widgetsConfig = widgetsConfig.filter((w) => w.key !== organisationWidget.type);
				}

				// Handle mandatory widgets
				const widget = widgetsConfig.find((w) => w.key === organisationWidget.type);
				if (widget && organisationWidget.state == 1) {
					widget.visible = true;
				}
			});
		}
	} catch (error) {
		log('error', 'WidgetLayout', `❌ Error filtering widgets by organisation: ${error.message}`);
	}

	return widgetsConfig;
}

function injectWidgetSettingsMenu() {
	let document = iframeDataBridge.getIframeWindow().document;
	const items = document.querySelectorAll('.nav-user-actions-sub-menu ul li');
	const hasItem = items.length > 0 && Array.from(items).some((item) => item.classList.contains('widget-settings-menu-item'));

	if (items.length >= 3 && !hasItem) {
		const li = document.createElement('li');
		li.classList.add('nav-user-actions-sub-menu-item');
		li.classList.add('widget-settings-menu-item');
		li.innerHTML = `
                <a href="#" id="widget-settings-menu">
                    <md-icon>widgets</md-icon>
                    <span>${t('WidgetsSettings')}</span>
                </a>
            `;
		items[0].after(li);

		document.getElementById('widget-settings-menu')?.addEventListener('click', (e) => {
			e.preventDefault();
			iframeDataBridge.getIframeWindow().parent.dispatchEvent(new CustomEvent('open-vue-widget-dialog'));
		});

		// Legacy (Knockout) widgets configuration. It used to be a floating gear in the top-right corner of
		// the widget layout; it now sits right under the Vue entry in the same menu. Desktop only, on purpose:
		// `.nav-user-actions-sub-menu` lives in `.nav-close-container-desktop`, which m.eagle hides below
		// 1025px, so the mobile menu (`.nav-open-container`) deliberately gets no legacy counterpart.
		const legacyLi = document.createElement('li');
		legacyLi.classList.add('nav-user-actions-sub-menu-item');
		legacyLi.classList.add('old-widget-settings-menu-item');
		legacyLi.innerHTML = `
                <a href="#" id="old-widget-settings-menu">
                    <md-icon>dashboard_customize</md-icon>
                    <span>${t('OldWidgetsSettings')}</span>
                </a>
            `;
		li.after(legacyLi);

		document.getElementById('old-widget-settings-menu')?.addEventListener('click', (e) => {
			e.preventDefault();
			oldWidgetConfigClick();
		});
	}

	const mobileMenu = document.querySelector('nav .nav-open-container');
	const mobileWidgetItem = mobileMenu?.querySelector('.mobile-widget-settings-menu-item');
	const hasMobileItem = mobileMenu && mobileWidgetItem;

	if (!hasMobileItem) {
		const ul = document.createElement('ul');
		const li = document.createElement('li');
		li.classList.add('mobile-widget-settings-menu-item');
		li.innerHTML = `
				<a href="#" id="mobile-widget-settings-menu">
					<md-icon>widgets</md-icon>
					<span>${t('WidgetsSettings')}</span>
				</a>
			`;
		ul.appendChild(li);

		const updateMenu = mobileMenu?.querySelector('.update-menu');
		if (updateMenu) {
			updateMenu.after(ul);
		} else if (mobileMenu) {
			mobileMenu.appendChild(ul);
		}

		document.getElementById('mobile-widget-settings-menu')?.addEventListener('click', (e) => {
			e.preventDefault();
			iframeDataBridge.getIframeWindow().parent.dispatchEvent(new CustomEvent('open-vue-widget-dialog'));
			document.querySelector('.nav-open-container').style.display = 'none';
		});
	}
}

function removePointsBadges(iframeDoc) {
	iframeDoc.querySelector('.points-user-badge-menu-item')?.remove();
	iframeDoc.querySelector('.mobile-points-user-badge-menu-item')?.remove();
}

async function injectPointsBadge() {
	const iframeDoc = iframeDataBridge.getIframeWindow().document;

	if (!isScoringMatrixAvailable()) {
		removePointsBadges(iframeDoc);
		return;
	}

	const pts = await getUserPoints();

	if (!pts?.season) {
		removePointsBadges(iframeDoc);
		return;
	}

	const totalPoints = formatPoints(pts?.total ?? 0);

	const desktopItems = iframeDoc.querySelectorAll('.nav-user-actions-sub-menu ul li');
	const hasDesktopBadge = desktopItems.length > 0 && Array.from(desktopItems).some((item) => item.classList.contains('points-user-badge-menu-item'));

	if (desktopItems.length >= 3 && !hasDesktopBadge) {
		const li = iframeDoc.createElement('li');
		li.classList.add('nav-user-actions-sub-menu-item', 'points-user-badge-menu-item');
		li.innerHTML = `
			<a href="#" id="points-user-badge-menu" style="display:flex;align-items:center;gap:6px;">
				<md-icon>paid</md-icon>
				<span id="points-user-badge-count">${t('YourPoints')} ${totalPoints} ${t('pts')}</span>
			</a>
		`;
		desktopItems[0].after(li);

		iframeDoc.getElementById('points-user-badge-menu')?.addEventListener('click', (e) => {
			e.preventDefault();
			iframeDataBridge.getIframeWindow().parent.dispatchEvent(new CustomEvent('open-vue-points-dialog'));
		});
	} else {
		const badge = iframeDoc.getElementById('points-user-badge-count');
		if (badge) badge.textContent = `${t('YourPoints')} ${totalPoints} ${t('pts')}`;
	}

	const mobileMenu = iframeDoc.querySelector('nav .nav-open-container');
	const hasMobileBadge = mobileMenu?.querySelector('.mobile-points-user-badge-menu-item');

	if (mobileMenu && !hasMobileBadge) {
		const ul = iframeDoc.createElement('ul');
		const li = iframeDoc.createElement('li');
		li.classList.add('mobile-points-user-badge-menu-item');
		li.innerHTML = `
			<a href="#" id="mobile-points-user-badge-menu" style="display:flex;align-items:center;gap:6px;">
				<md-icon>paid</md-icon>
				<span id="mobile-points-user-badge-count">${t('YourPoints')} ${totalPoints} ${t('pts')}</span>
			</a>
		`;
		ul.appendChild(li);

		const widgetMenu = mobileMenu.querySelector('.mobile-widget-settings-menu-item')?.parentElement;
		if (widgetMenu) {
			widgetMenu.after(ul);
		} else {
			mobileMenu.appendChild(ul);
		}

		iframeDoc.getElementById('mobile-points-user-badge-menu')?.addEventListener('click', (e) => {
			e.preventDefault();
			iframeDataBridge.getIframeWindow().parent.dispatchEvent(new CustomEvent('open-vue-points-dialog'));
			iframeDoc.querySelector('.nav-open-container').style.display = 'none';
		});
	}
}

function setWidgetSettingsGridData() {
	widgetSettingsGridColumns.value = [
		{ field: 'index', headerName: t('Position'), width: 100, rowDrag: true, sortable: false, filter: false },
		{ field: 'title', headerName: t('Title'), width: 300, sortable: false, filter: false },
		{ field: 'columns', headerName: `${t('Width')}`, width: 120, sortable: false, filter: false, editable: true, cellEditor: 'agNumberCellEditor', cellEditorParams: (params) => ({ min: getWidgetMinSpan(params.data.id, 'columns'), max: 6, step: 1, showStepperButtons: true }), valueParser: parseGridSpanValue },
		{ field: 'rows', headerName: `${t('Height')}`, width: 120, sortable: false, filter: false, editable: true, cellEditor: 'agNumberCellEditor', cellEditorParams: (params) => ({ min: getWidgetMinSpan(params.data.id, 'rows'), max: 6, step: 1, showStepperButtons: true }), valueParser: parseGridSpanValue },
		{ field: 'visible', headerName: t('Visible'), width: 180, sortable: false, filter: false, cellRenderer: VisibilitySwitchRenderer },
		{
			field: 'delete',
			headerName: '',
			width: 80,
			minWidth: 80,
			maxWidth: 80,
			sortable: false,
			filter: false,
			cellRendererSelector: (params) => {
				if (!params.data.custom && !params.data.guid) return null;
				return { component: ButtonIconRenderer };
			},
			cellRendererParams: (params) => ({
				icon: 'delete',
				tooltip: t('Delete'),
				key: params.data.id,
				onClick: (key) => {
					const index = sortedWidgets.value.findIndex((w) => w.id === key);
					if (index !== -1) {
						sortedWidgets.value.splice(index, 1);
					}
					saveWidgetSettings();
				},
			}),
			suppressHeaderMenuButton: true,
		},
	];
}

function parseGridSpanValue(params) {
	const parsed = Number.parseInt(params.newValue, 10);
	const widgetParams = params.data.params;
	const min = getWidgetMinSpan(params.data.id, params.colDef.field === 'rows' ? 'rows' : 'columns');

	if (Number.isNaN(parsed)) {
		return params.oldValue;
	}

	const result = Math.min(Math.max(parsed, min), 6);

	// update same size if circle
	if (widgetParams && widgetParams.shape === 'circle') {
		// update row column and row
		if (params.colDef.field === 'columns') {
			params.data.rows = result;
		} else if (params.colDef.field === 'rows') {
			params.data.columns = result;
		}
	}

	return result;
}

async function saveWidgetSettings(closeDialog = true) {
	widgetSettingsGrid.value?.getGridApi()?.stopEditing();
	const updatedWidgetsConfig = sortedWidgets.value.map((widget) => {
		const isCustom = !baseWidgetIds.has(widget.id);
		return {
			id: widget.id,
			visible: widget.visible,
			columns: clampGridSpan(widget.columns, widget.id, 'columns'),
			rows: clampGridSpan(widget.rows, widget.id, 'rows'),
			index: widget.index,
			guid: widget.guid,
			...(widget.params && 'simulated' in widget.params ? { simulated: widget.params.simulated } : {}),
			...(isCustom ? { title: widget.title, componentKey: widget.componentKey, params: widget.params } : {}),
		};
	});

	userSettings.value = userSettings.value.filter((s) => s.name !== 'VueWidgets');
	userSettings.value.push({
		name: 'VueWidgets',
		groupName: 'WEBAPP',
		value: JSON.stringify(updatedWidgetsConfig),
	});
	await postUserSettings(userSettings.value, { groupName: 'WEBAPP', userId: user.id });

	if (closeDialog) widgetSettingsDialog.value = false;
}

async function resetWidgetSettings() {
	try {
		userSettings.value = userSettings.value.filter((s) => s.name !== 'VueWidgets');
		await postUserSettings(userSettings.value, { groupName: 'WEBAPP', userId: user.id });

		widgetsConfig = [];
		await getWidgetSettings();
		setWidgetSettingsGridData();

		widgetSettingsDialog.value = false;
		log('info', 'WidgetLayout', 'Widget settings reset to defaults');
	} catch (error) {
		log('error', 'WidgetLayout', `Error resetting widget settings: ${error.message}`);
	}
}

function onKpiSimulatedChanged({ guid, reference, isSimulated }) {
	const normalizedReference = String(reference || '').toLowerCase();
	const widget = !guid ? sortedWidgets.value.find((w) => String(w?.params?.reference || '').toLowerCase() === normalizedReference) : sortedWidgets.value.find((w) => w.id === guid);
	if (widget?.params) {
		widget.params.simulated = isSimulated;
		saveWidgetSettings(false);
	}
}

function normalizeCustomKpiParams(params = {}, widgetsCollection = sortedWidgets.value) {
	// SPS customs are a saved filter set only (no reference/objective/simulated/icon) — keep them intact
	// so the pinned WidgetSps reproduces exactly that filtered score.
	if (params?.type === 'sps') {
		return {
			type: 'sps',
			shape: 'circle',
			organisations: params.organisations || [],
			accounts: params.accounts || [],
			channels: params.channels || [],
			deliveryLocationTypes: params.deliveryLocationTypes || params.dlts || [],
		};
	}

	const normalizedParams = { type: 'kpi', shape: 'circle', ...params };
	const sourceReference = params?.reference;
	const sourceWidget = sourceReference && Array.isArray(widgetsCollection) ? widgetsCollection.find((widget) => !widget?.guid && widget?.params?.reference?.toLowerCase() === sourceReference.toLowerCase()) : null;
	const sourceKpi = sourceReference ? (iframeDataBridge.getEcosVM()?.kpis || []).find((kpi) => kpi?.reference?.toLowerCase() === sourceReference.toLowerCase()) : null;

	normalizedParams.icon = normalizedParams.icon || sourceWidget?.params?.icon || sourceKpi?.icon || 'star';
	const sourceSupportsSimulation = !!(sourceWidget?.params && 'simulated' in sourceWidget.params);

	if (sourceSupportsSimulation) {
		normalizedParams.simulated = normalizedParams.simulated ?? false;
	} else {
		delete normalizedParams.simulated;
	}

	return normalizedParams;
}

function onKpiCustomSaved({ guid, params, annotation, addedToHome }) {
	const existingWidgetIndex = sortedWidgets.value.findIndex((w) => w.id === guid);
	const customParams = normalizeCustomKpiParams(params);
	const componentKey = params?.type === 'sps' ? 'WidgetSps' : 'WidgetKpi';

	if (existingWidgetIndex !== -1) {
		// Update existing custom KPI
		if (addedToHome) {
			sortedWidgets.value[existingWidgetIndex] = { id: guid, componentKey, title: annotation, visible: true, mandatory: false, columns: 1, rows: 1, index: sortedWidgets.value.length + 1, guid: guid, params: customParams };
		} else {
			// "Retirer de la page d'accueil" is only reachable from within the widget's own fullscreen
			// KpiBuilder/SpsBuilder. Splicing it out directly would force-unmount it before it ever calls
			// toggleFullscreen(), so widget-fullscreened never re-fires and resizeLayout never restores the
			// legacy .wcolumn widgets it hid. Close it gracefully first, like onPersistentZoneReplaced does.
			const removedWidgetInstance = widgetRefs.get(guid.toLowerCase());
			if (removedWidgetInstance?.isFullscreen && typeof removedWidgetInstance.toggleFullscreen === 'function') {
				removedWidgetInstance.toggleFullscreen();
			}
			sortedWidgets.value.splice(existingWidgetIndex, 1);
		}
	} else if (addedToHome) {
		// Add new custom KPI
		sortedWidgets.value.push({ id: guid, componentKey, title: annotation, visible: true, mandatory: false, columns: 1, rows: 1, index: sortedWidgets.value.length + 1, guid: guid, params: customParams });
	}

	saveWidgetSettings(false);
}

function oldWidgetConfigClick() {
	const iframeWindow = iframeDataBridge.getIframeWindow();
	const legacyWidgetsVM = iframeWindow.externis?.sales?.vwWidgetsVM;

	if (!legacyWidgetsVM) {
		log('warn', 'WidgetLayout', 'Legacy widgets view-model unavailable — old widgets settings skipped');
		return;
	}

	// The legacy list is a Bootstrap modal at z-index 1050, i.e. *below* the legacy nav (1110), and the
	// user menu only closes on a click outside itself — so close it here or it floats over the modal.
	const subMenu = iframeWindow.document.querySelector('.nav-user-actions-sub-menu');
	if (subMenu) subMenu.style.display = 'none';

	legacyWidgetsVM.widgetSettingsHandler();
}

// #endregion

// #region UI

async function onPersistentZoneReplaced() {
	await nextTick();
	if (!fullscreenWidget.value) return;

	for (const instance of widgetRefs.values()) {
		if (instance?.isFullscreen && typeof instance.toggleFullscreen === 'function') {
			instance.toggleFullscreen();
			break;
		}
	}
}

function handleWindowResize() {
	activeGridColumns.value = getGridColumnsForViewport(window.innerWidth);
	const document = iframeDataBridge.getIframeWindow().document;
	const widgetLayout = document.querySelector('.widget-layout');

	if (widgetLayout && widgetLayout.style.height !== 'auto') {
		const hasFullScreenWidget = document.querySelector('.widget.fullscreen-widget');
		resizeLayout({ fullScreened: !!hasFullScreenWidget, widget: getFullscreenWidgetKey(document) });
	}

	isTouchMode.value = deviceUtils.isTouchMode();
}

function getFullscreenWidgetKey(document, payload = null) {
	if (payload?.fullScreened === false) {
		return null;
	}

	if (payload?.widget) {
		return payload.widget;
	}

	const fullscreenWidgetElement = document.querySelector('.widget.fullscreen-widget');
	if (!fullscreenWidgetElement) {
		return null;
	}

	return Array.from(fullscreenWidgetElement.classList).find((className) => className.startsWith('widget-') && className !== 'widget-hidden') || null;
}

function resizeLayout(payload) {
	const document = iframeDataBridge.getIframeWindow().document;
	const layout = document.querySelector('.widget-layout');
	const activeFullscreenWidget = getFullscreenWidgetKey(document, payload);
	const isMapWidget = MAP_WIDGET_IDS.includes(activeFullscreenWidget);

	const pageHeaderContent = document.querySelector('.page-header-content');
	const mainNav = document.querySelector('main-nav nav');
	const contentWrapper = document.querySelector('.content-wrapper');
	const oldWidgets = iframeDocument.querySelectorAll('.wcolumn');

	fullscreenWidget.value = activeFullscreenWidget;

	if (layout) {
		const buffer = deviceUtils.isMobile() ? -30 : 0;
		const height = document.documentElement.clientHeight - (pageHeaderContent && isVisible(pageHeaderContent) ? pageHeaderContent.offsetHeight : 0) - (mainNav && isVisible(mainNav) ? mainNav.offsetHeight : 0) - buffer;

		if (isMapWidget && isTouchMode.value && payload?.fullScreened) {
			layout.style.height = '';
		} else {
			layout.style.height = payload?.fullScreened ? `${height}px` : 'auto';
		}
		const contentWrapperHeight = contentWrapper ? document.documentElement.clientHeight - (mainNav ? mainNav.offsetHeight : 0) : 0;
		contentWrapper.style.height = payload?.fullScreened ? `${contentWrapperHeight}px` : '100%';
		contentWrapper.style.overflowY = payload?.fullScreened ? 'hidden' : 'auto';

		oldWidgets.forEach((widget) => {
			widget.style.display = payload?.fullScreened ? 'none' : 'block';
		});

		requestAnimationFrame(async () => {
			if (payload?.callback) {
				await payload.callback();
			}

			contentWrapper.scrollTo({ top: 0, behavior: 'smooth' });
		});
	}
}

function isVisible(el) {
	return el && window.getComputedStyle(el).display !== 'none';
}

// #endregion
</script>
